'use client';
import { useTypedTranslation } from '@/hooks/useTypedTranslation';
import styles from './MerchantsSponsors.module.css';
import Image from 'next/image';
import { Suspense, useEffect, useState } from 'react';
import { useSearchParams } from 'next/navigation';

// Default export that wraps the content in Suspense
export default function MerchantsSponsors() {
  return (
    <Suspense
      fallback={
        <div className={styles.sponsorsSection}>
          <div className={styles.container}>Loading...</div>
        </div>
      }
    >
      <MerchantsSponsorsContent />
    </Suspense>
  );
}

// Component that uses useSearchParams wrapped in Suspense
function MerchantsSponsorsContent() {
  const { t } = useTypedTranslation();
  const searchParams = useSearchParams();
  const [isSpanishMarket, setIsSpanishMarket] = useState(true); // Default to Spanish market
  const [isClient, setIsClient] = useState(false);

  // Set client flag after hydration
  useEffect(() => {
    setIsClient(true);
  }, []);

  // Check URL for market parameter and update when it changes
  useEffect(() => {
    if (isClient) {
      const marketParam = searchParams.get('market');
      // Spanish market is when there's no market parameter
      // Only show sponsors section for Spanish market (no query param)
      setIsSpanishMarket(marketParam === null);
    }
  }, [searchParams, isClient]); // Re-run effect when searchParams changes

  // Only render the sponsors section for Spanish market and after client hydration
  if (!isClient || !isSpanishMarket) {
    return null;
  }

  return (
    <section className={styles.sponsorsSection}>
      <div className={styles.container}>
        <h2 className={styles.sponsorsTitle}> {t('navbar.sponsorsAndAwards')}</h2>
        {/* EU Funding Banner */}
        <div className={styles.euFundingBanner}>
          <div className={styles.acceleratorsContainer}>
            <div className={styles.acceleratorLogoItem}>
              <Image
                src="/images/sponsors/fondos-europeos.webp"
                alt="Fondos Europeos"
                width={180}
                height={60}
                className={styles.logoImage}
                style={{ objectFit: 'contain' }}
              />
            </div>
            <div className={styles.acceleratorLogoItem}>
              <Image
                src="/images/sponsors/comunidad-de-madrid.webp"
                alt="Comunidad de Madrid"
                width={180}
                height={60}
                className={styles.logoImage}
                style={{ objectFit: 'contain' }}
              />
            </div>
            <div className={styles.acceleratorLogoItem}>
              <Image
                src="/images/sponsors/confinanciado-por-la-union-europea.webp"
                alt="Cofinanciado por la Unión Europea"
                width={180}
                height={60}
                className={styles.logoImage}
                style={{ objectFit: 'contain' }}
              />
            </div>
          </div>
          <p className={styles.euFundingText}>
            Files 09-GCE1-03136.2/2024 and 09-GCE1-03524.1/2024, from Line 2 of stable hiring for
            young people, within the Aid Program for Employment Promotion in the Community of
            Madrid, through which the Community of Madrid, with co-financing from the European
            Union, has granted subsidies for two hires.
          </p>
        </div>
        <div className={styles.sponsorContainer}>
          {/* Investment Section */}

          <div className={styles.investmentSection}>
            <h3 className={styles.categoryTitle}>{t('merchants.sponsors.investment.title')}</h3>
            <div className={styles.acceleratorsContainer}>
              <div className={styles.acceleratorLogoItem}>
                <Image
                  src="/images/sponsors/ministerio.webp"
                  alt="Ministerio"
                  width={180}
                  height={60}
                  className={styles.logoImage}
                  style={{ objectFit: 'contain' }}
                />
              </div>
              <div className={styles.acceleratorLogoItem}>
                <Image
                  src="/images/sponsors/enisa.webp"
                  alt="ENISA"
                  width={180}
                  height={60}
                  className={styles.logoImage}
                  style={{ objectFit: 'contain' }}
                />
              </div>
              <div className={styles.acceleratorLogoItem}>
                <Image
                  src="/images/sponsors/BMEX.webp"
                  alt="BME"
                  width={180}
                  height={60}
                  className={styles.logoImage}
                  style={{ objectFit: 'contain' }}
                />
              </div>
            </div>
          </div>
          {/* Support Section */}

          <div className={styles.investmentSection}>
            <h3 className={styles.categoryTitle}>{t('merchants.sponsors.support.title')}</h3>
            <div className={styles.acceleratorsContainer}>
              <div className={styles.acceleratorLogoItem}>
                <Image
                  src="/images/sponsors/union_europea_fondos_next_logo.jpg"
                  alt="Union Europea fondos next"
                  width={180}
                  height={60}
                  className={styles.logoImage}
                  style={{ objectFit: 'contain' }}
                />
              </div>
              <div className={styles.acceleratorLogoItem}>
                <Image
                  src="/images/sponsors/ministerio_de_la_transformacion_digital_logo.jpg"
                  alt="Ministerio de la transformacion digital logo"
                  width={180}
                  height={60}
                  className={styles.logoImage}
                  style={{ objectFit: 'contain' }}
                />
              </div>
              <div className={styles.acceleratorLogoItem}>
                <Image
                  src="/images/sponsors/plan_de_recuperacion_transformacion_resiliencia_logo.jpg"
                  alt="Plan e recuperación Transformación y Resiliencia logo"
                  width={180}
                  height={60}
                  className={styles.logoImage}
                  style={{ objectFit: 'contain' }}
                />
              </div>
              <div className={styles.acceleratorLogoItem}>
                <Image
                  src="/images/sponsors/comunidad_de_madrid_logo.jpg"
                  alt="Comunidad de Madrid logo"
                  width={180}
                  height={60}
                  className={styles.logoImage}
                  style={{ objectFit: 'contain' }}
                />
              </div>
            </div>
            <p className={styles.supportText}>{t('merchants.sponsors.support.text')}</p>
          </div>

          {/* Blockchain Section */}

          <div className={styles.investmentSection}>
            <h3 className={styles.categoryTitle}>{t('merchants.sponsors.blockchain.title')}</h3>
            <div className={styles.acceleratorsContainer}>
              <div className={styles.acceleratorLogoItem}>
                <Image
                  src="/images/sponsors/union_europea_fondos_next_logo.jpg"
                  alt="Union Europea fondos next"
                  width={180}
                  height={60}
                  className={styles.logoImage}
                  style={{ objectFit: 'contain' }}
                />
              </div>
              <div className={styles.acceleratorLogoItem}>
                <Image
                  src="/images/sponsors/ministerio_de_la_transformacion_digital_logo.jpg"
                  alt="Ministerio de la transformacion digital logo"
                  width={180}
                  height={60}
                  className={styles.logoImage}
                  style={{ objectFit: 'contain' }}
                />
              </div>
              <div className={styles.acceleratorLogoItem}>
                <Image
                  src="/images/sponsors/plan_de_recuperacion_transformacion_resiliencia_logo.jpg"
                  alt="Plan e recuperación Transformación y Resiliencia logo"
                  width={180}
                  height={60}
                  className={styles.logoImage}
                  style={{ objectFit: 'contain' }}
                />
              </div>
              <div className={styles.acceleratorLogoItem}>
                <Image
                  src="/images/sponsors/comunidad_de_madrid_logo.jpg"
                  alt="Comunidad de Madrid logo"
                  width={180}
                  height={60}
                  className={styles.logoImage}
                  style={{ objectFit: 'contain' }}
                />
              </div>
            </div>
            <p className={styles.supportText}>{t('merchants.sponsors.blockchain.text')}</p>
          </div>

          {/* Accelerators Section */}
          <div className={styles.acceleratorsSection}>
            <h3 className={styles.categoryTitle}>{t('merchants.sponsors.accelerators.title')}</h3>
            <div className={styles.acceleratorsContainer}>
              <div className={styles.acceleratorLogoItem}>
                <Image
                  src="/images/sponsors/viveros-de-empresas.webp"
                  alt="Viveros de Empresas"
                  width={180}
                  height={60}
                  className={styles.logoImage}
                  style={{ objectFit: 'contain' }}
                />
              </div>
              <div className={styles.acceleratorLogoItem}>
                <Image
                  src="/images/sponsors/santander-x100.webp"
                  alt="Santander X100"
                  width={180}
                  height={60}
                  className={styles.logoImage}
                  style={{ objectFit: 'contain' }}
                />
              </div>
              <div className={styles.acceleratorLogoItem}>
                <Image
                  src="/images/sponsors/wayra.webp"
                  alt="Wayra"
                  width={180}
                  height={60}
                  className={styles.logoImage}
                  style={{ objectFit: 'contain' }}
                />
              </div>
              <div className={styles.acceleratorLogoItem}>
                <Image
                  src="/images/sponsors/lanzadera.webp"
                  alt="Lanzadera"
                  width={180}
                  height={60}
                  className={styles.logoImage}
                  style={{ objectFit: 'contain' }}
                />
              </div>
              <div className={styles.acceleratorLogoItem}>
                <Image
                  src="/images/sponsors/unicorn.webp"
                  alt="Unicorn"
                  width={180}
                  height={60}
                  className={styles.logoImage}
                  style={{ objectFit: 'contain' }}
                />
              </div>
            </div>
          </div>
          {/* Awards Section */}
          <div className={styles.awardsSection}>
            <h3 className={styles.categoryTitle}>{t('merchants.sponsors.awards.title')}</h3>
            <div className={styles.awardsContainer}>
              <div className={styles.awardLogoItem}>
                <Image
                  src="/images/sponsors/expansion-premio.webp"
                  alt="Expansion Premio"
                  width={180}
                  height={60}
                  className={styles.logoImage}
                  style={{ objectFit: 'contain' }}
                />
              </div>
              <div className={styles.awardLogoItem}>
                <Image
                  src="/images/sponsors/visa-innovation-program.webp"
                  alt="Visa Innovation Program"
                  width={180}
                  height={60}
                  className={styles.logoImage}
                  style={{ objectFit: 'contain' }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
